import * as React from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/utils';

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface DateRangePickerProps {
  value?: DateRange;
  onChange?: (range: DateRange) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  onApply?: () => void;
  mode?: 'default' | 'weekly';
  disableClear?: boolean;
}

// Utilities
const startOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

const endOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

const formatDate = (date: Date) => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

const PRESET_RANGES = [
  {
    label: 'Today',
    value: 'today',
    getRange: () => ({
      from: startOfDay(new Date()),
      to: endOfDay(new Date()),
    }),
  },
  {
    label: 'Yesterday',
    value: 'yesterday',
    getRange: () => {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    },
  },
  {
    label: 'Last 7 days',
    value: 'last-7',
    getRange: () => {
      const from = new Date();
      from.setDate(from.getDate() - 6);
      return { from: startOfDay(from), to: endOfDay(new Date()) };
    },
  },
  {
    label: 'Last 30 days',
    value: 'last-30',
    getRange: () => {
      const from = new Date();
      from.setDate(from.getDate() - 29);
      return { from: startOfDay(from), to: endOfDay(new Date()) };
    },
  },
  {
    label: 'Last 90 days',
    value: 'last-90',
    getRange: () => {
      const from = new Date();
      from.setDate(from.getDate() - 89);
      return { from: startOfDay(from), to: endOfDay(new Date()) };
    },
  },
  {
    label: 'This month',
    value: 'this-month',
    getRange: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: startOfDay(from), to: endOfDay(to) };
    },
  },
];

export const getWeekRange = (date: Date) => {
  const d = new Date(date);

  // JS: Sunday = 0, Monday = 1
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;

  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMonday);
  monday.setHours(0, 0, 0, 0);

  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  return { from: monday, to: sunday };
};

export function DateRangePicker({
  value,
  onChange,
  placeholder = 'mm / dd / yy',
  className,
  disabled = false,
  onApply,
  mode = 'default',
  disableClear = false,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [fromInput, setFromInput] = React.useState<string>('');
  const [toInput, setToInput] = React.useState<string>('');
  const [popupStyle, setPopupStyle] = React.useState<React.CSSProperties>({});
  const popupRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (popupRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;

      const radixPortal = document.querySelector('[data-radix-popper-content-wrapper]');
      if (radixPortal && radixPortal.contains(target)) return;
      setIsOpen(false);
    }

    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const openPicker = () => {
    if (disabled) return;
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const POPUP_WIDTH = 340;
      const margin = 8;
      const width = Math.min(POPUP_WIDTH, window.innerWidth - margin * 2);
      let left = rect.right - width;
      if (left < margin) left = margin;
      setPopupStyle({ top: rect.bottom + 4, left, width });
    }
    setIsOpen(true);
  };

  // Sync with parent value
  const formatForInput = (date: Date) => date.toLocaleDateString('en-CA');

  React.useEffect(() => {
    setFromInput(value?.from ? formatForInput(value.from) : '');
    setToInput(value?.to ? formatForInput(value.to) : '');
  }, [value]);

  const handlePresetChange = (presetValue: string) => {
    const preset = PRESET_RANGES.find((p) => p.value === presetValue);
    if (preset && onChange) {
      onChange(preset.getRange());
    }
  };

  const handleFromChange = (v: string) => {
    setFromInput(v);
    if (!v) {
      onChange?.({ from: undefined, to: value?.to });
      return;
    }

    const selected = new Date(v);

    if (mode === 'weekly') {
      onChange?.(getWeekRange(selected));
    } else {
      selected.setHours(0, 0, 0, 0);
      onChange?.({ from: selected, to: value?.to });
    }
  };

  const handleToChange = (v: string) => {
    setToInput(v);
    if (!v) {
      onChange?.({ from: value?.from, to: undefined });
      return;
    }

    const selected = new Date(v);

    if (mode === 'weekly') {
      onChange?.(getWeekRange(selected));
    } else {
      selected.setHours(23, 59, 59, 999);
      onChange?.({ from: value?.from, to: selected });
    }
  };

  const clearRange = () => {
    setFromInput('');
    setToInput('');
    onChange?.({ from: undefined, to: undefined });
  };

  const formatDisplay = (d?: Date) => (d ? formatDate(d) : placeholder);

  const hasError = value?.from && value?.to && value.from.getTime() > value.to.getTime();

  const shortDate = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });

  return (
    <div className={cn('relative w-full', className)}>
      {/* TRIGGER */}
      <div
        ref={triggerRef}
        onClick={openPicker}
        className={cn(
          'h-8 sm:h-[38px] flex items-center w-full bg-white text-gray-800 rounded-xl border border-gray-200 px-2 sm:px-2.5 py-1 cursor-pointer shadow-sm hover:border-gray-300 transition-colors',
          hasError ? 'border-red-400' : '',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 font-medium">
          <CalendarDays className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 rounded-sm p-1 bg-icon-gradient text-white" />

          {/* Compact on mobile */}
          <span className="sm:hidden text-xs text-gray-600 whitespace-nowrap">
            {value?.from && value?.to ? (
              `${shortDate(value.from)} → ${shortDate(value.to)}`
            ) : (
              <span className="text-gray-400">Date range</span>
            )}
          </span>

          {/* Full display on sm+ */}
          <span className="hidden sm:flex items-center gap-2 min-w-0 text-sm">
            <span className={cn('truncate', !value?.from ? 'text-gray-400' : 'text-gray-700')}>
              {value?.from ? formatDisplay(value.from) : placeholder}
            </span>
            <span className="text-gray-400 flex-shrink-0">→</span>
            <span className={cn('truncate', !value?.to ? 'text-gray-400' : 'text-gray-700')}>
              {value?.to ? formatDisplay(value.to) : placeholder}
            </span>
          </span>
        </div>
      </div>

      {/* ERROR */}
      {hasError && <p className="text-sm text-red-500 mt-1">Start date cannot be after end date</p>}

      {/* POPUP — rendered in a portal so it's never clipped by overflow */}
      {isOpen &&
        createPortal(
          <div ref={popupRef} style={{ ...popupStyle, position: 'fixed', zIndex: 9999 }}>
            <Card className="w-full shadow-xl">
              <CardContent className="p-3 space-y-4 bg-black text-white border border-primary-orange rounded-lg">
                {mode === 'default' && (
                  <div>
                    <Label className="text-gray-700">Quick Select</Label>
                    <Select onValueChange={handlePresetChange}>
                      <SelectTrigger className="w-full mt-1 bg-white text-black border border-primary-orange">
                        <SelectValue placeholder="Choose preset" />
                      </SelectTrigger>
                      <SelectContent className="z-[99999]">
                        {PRESET_RANGES.map((p) => (
                          <SelectItem key={p.value} value={p.value}>
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Custom Range */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-gray-700">From</Label>
                    <Input
                      type="date"
                      className="bg-white text-black rounded-md"
                      value={fromInput}
                      onChange={(e) => handleFromChange(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">To</Label>
                    <Input
                      type="date"
                      className="bg-white text-black rounded-md"
                      value={toInput}
                      onChange={(e) => handleToChange(e.target.value)}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 border-t border-gray-200 pt-3">
                  {!disableClear && (
                    <Button variant="outline" size="sm" onClick={clearRange}>
                      Clear
                    </Button>
                  )}
                  <Button
                    variant="gradient"
                    size="sm"
                    onClick={() => {
                      onApply?.();
                      setIsOpen(false);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>,
          document.body
        )}
    </div>
  );
}
